import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ReactNode } from 'react';
import { isValidElement, useRef, useState } from 'react';

interface SelectSearchProps<T> {
  value: string;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  items: { id: string; label: ReactNode }[];
  children?: ReactNode;
  onSearchChange?: (search: string) => void;
}

export function SelectSearch<T>({
  value,
  onValueChange,
  onOpenChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  emptyMessage = 'No se encontraron resultados.',
  items,
  children,
  onSearchChange,
}: SelectSearchProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Select
      open={open}
      disabled={disabled}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        onOpenChange?.(isOpen);
        if (isOpen) {
          setTimeout(() => inputRef.current?.focus(), 0);
          if (!onSearchChange) {
            setSearch('');
          }
        }
      }}
      value={value}
      onValueChange={(val) => {
        onValueChange(val);
        setOpen(false);
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            const viewport = e.currentTarget.querySelector(
              '[data-radix-select-viewport]'
            );
            const firstItem = viewport?.querySelector(
              '[role="option"]'
            ) as HTMLElement;
            if (document.activeElement === firstItem) {
              e.preventDefault();
              inputRef.current?.focus();
            }
          }
        }}
      >
        <div className="p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder={searchPlaceholder}
              className="pl-8"
              value={search}
              onChange={(e) => {
                const newSearch = e.target.value;
                setSearch(newSearch);
                onSearchChange?.(newSearch);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const viewport =
                    e.currentTarget.closest('[data-radix-select-viewport]');
                  const firstItem =
                    viewport?.querySelector('[role="option"]') as HTMLElement;
                  firstItem?.focus();
                }
                if (e.key === 'Enter' && items.length === 1) {
                  e.preventDefault();
                  onValueChange(items[0].id);
                  setOpen(false);
                }
                if (e.key === 'Tab') {
                  setOpen(false);
                }
              }}
              autoComplete="off"
            />
          </div>
        </div>
        {items.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        )}
        {items.map((item) => (
          <SelectItem key={item.id} value={item.id}>
            {item.label}
          </SelectItem>
        ))}
        {children}
      </SelectContent>
    </Select>
  );
}
